import useSWR from "swr";

import {
  generateSignedM3u8Url,
  generateSignedUrl,
} from "./generatePreSignedUrl";
import { parseM3u8ToExtractThumbnailManifestFileName } from "./m3u8Parser";

import type { Cookie } from "@spwn/types/functions";
import type { SWRResponse } from "swr";

const manifestCache = new Map<string, string>();

/** テスト用: キャッシュをクリアする */
export const clearManifestCache = (): void => {
  manifestCache.clear();
};

export const fetchThumbnailManifest = async ({
  cookie: { url: cookieUrl, cookie },
}: {
  cookie: Pick<Cookie, "url" | "cookie">;
}): Promise<string | undefined> => {
  try {
    const manifestFileUrl = generateSignedM3u8Url(cookieUrl, cookie);

    let manifestFileData = manifestCache.get(manifestFileUrl);
    if (!manifestFileData) {
      const response = await fetch(manifestFileUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      manifestFileData = await response.text();
      manifestCache.set(manifestFileUrl, manifestFileData);
    }
    const thumbnailManifestFileName =
      parseM3u8ToExtractThumbnailManifestFileName(manifestFileData);

    if (!thumbnailManifestFileName) {
      console.warn("No thumbnail manifest file name found.");
      return undefined;
    }

    const thumbnailIndexUrl = generateSignedUrl({
      manifestFileUrl: cookieUrl,
      cookie,
      fileName: thumbnailManifestFileName,
    });

    const thumbnailManifestFileResponse = await fetch(thumbnailIndexUrl);

    if (!thumbnailManifestFileResponse.ok) {
      throw new Error(
        `HTTP error! status: ${thumbnailManifestFileResponse.status}`
      );
    }
    const thumbnailManifestFileData =
      await thumbnailManifestFileResponse.text();

    return thumbnailManifestFileData;
  } catch (error) {
    console.error("Error fetching thumbnail manifest:", error);
    return undefined;
  }
};

export const useThumbnailManifest = ({
  cookie,
  refreshInterval,
  enabled = true,
}: {
  cookie: Pick<Cookie, "url" | "cookie">;
  refreshInterval?: number;
  enabled?: boolean;
}): SWRResponse<string | undefined, Error> => {
  const cookieKey = JSON.stringify(cookie.cookie ?? {});
  return useSWR(
    ["thumbnailManifest", cookieKey, cookie.url],
    async () => fetchThumbnailManifest({ cookie }),
    {
      // キャッシュデータを即座に返し、バックグラウンドで再検証
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshInterval: enabled ? refreshInterval : 0,
      isPaused: () => !enabled,
    }
  );
};
